import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Pencil, RotateCcw, Save, X } from "lucide-react";
import { LeaderPortrait } from "../components/LeaderPortrait";
import { CONTACT, PEOPLE } from "../data/content";

export default function LeaderBio() {
  const { slug } = useParams();
  const person = PEOPLE.find((item) => item.slug === slug);
  const storageKey = `clach-leader-bio-${slug}`;
  const [override, setOverride] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ experience: "", focusAreas: "" });

  useEffect(() => {
    try {
      setOverride(JSON.parse(localStorage.getItem(storageKey)) || null);
    } catch {
      setOverride(null);
    }
    setEditing(false);
  }, [storageKey]);

  const bio = useMemo(
    () => ({
      experience: override?.experience || person?.experience,
      focusAreas: override?.focusAreas || person?.focusAreas || [],
    }),
    [override, person],
  );

  if (!person) return <Navigate to="/about" replace />;

  const startEditing = () => {
    setDraft({ experience: bio.experience, focusAreas: bio.focusAreas.join(", ") });
    setEditing(true);
  };

  const saveBio = () => {
    const next = {
      experience: draft.experience.trim(),
      focusAreas: draft.focusAreas.split(",").map((item) => item.trim()).filter(Boolean),
    };
    localStorage.setItem(storageKey, JSON.stringify(next));
    setOverride(next);
    setEditing(false);
  };

  const resetBio = () => {
    localStorage.removeItem(storageKey);
    setOverride(null);
    setEditing(false);
  };

  return (
    <main data-testid={`leader-bio-${person.slug}`} className="min-h-screen bg-[#070C14] px-4 pb-24 pt-32 text-white sm:px-8 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <Link to="/about" data-testid="back-to-leadership-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-blue-300 transition-colors hover:text-amber-300">
          <ArrowLeft size={16} /> Clach people
        </Link>

        <section className="mt-10 grid gap-10 lg:grid-cols-[.85fr_1.15fr] lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -32 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}>
            <LeaderPortrait person={person} large />
            <div className="mt-6 rounded-3xl border border-white/10 bg-[#0D131F] p-6">
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-amber-400">Direct line</p>
              <a href={`tel:${CONTACT.phones[0].replace(/\s/g, "")}`} data-testid="leader-phone-link" className="mt-3 block font-heading text-2xl font-semibold text-white transition-colors hover:text-amber-300">
                {CONTACT.phones[0]}
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-amber-400">Leadership biography</p>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl" data-testid="leader-name">{person.name}</h1>
            <p className="mt-4 text-xl text-blue-300" data-testid="leader-role">{person.role}</p>

            <div className="mt-10 rounded-3xl border border-white/10 bg-[#0D131F] p-7 sm:p-9">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-heading text-2xl font-semibold">Experience</h2>
                <div className="flex gap-3">
                  {!editing ? (
                    <button type="button" onClick={startEditing} data-testid="edit-leader-bio-button" className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber-300 transition-colors hover:bg-amber-400 hover:text-[#111827]">
                      <Pencil size={14} /> Edit bio
                    </button>
                  ) : (
                    <>
                      <button type="button" onClick={saveBio} data-testid="save-leader-bio-button" className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#111827]">
                        <Save size={14} /> Save
                      </button>
                      <button type="button" onClick={() => setEditing(false)} data-testid="cancel-leader-bio-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">
                        <X size={14} /> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              {override && !editing && (
                <p data-testid="local-edit-notice" className="mt-4 rounded-full border border-blue-400/25 bg-blue-400/10 px-4 py-2 text-xs text-blue-200">
                  Edited locally in this browser
                </p>
              )}

              {!editing ? (
                <p className="mt-6 text-base leading-8 text-slate-300" data-testid="leader-experience-text">{bio.experience}</p>
              ) : (
                <textarea
                  value={draft.experience}
                  onChange={(event) => setDraft((current) => ({ ...current, experience: event.target.value }))}
                  data-testid="leader-experience-input"
                  rows={7}
                  className="mt-6 w-full rounded-2xl border border-white/10 bg-[#070C14] p-4 text-base leading-8 text-white outline-none focus:border-amber-400"
                />
              )}
            </div>

            <div className="mt-6 rounded-3xl border border-white/10 bg-[#0D131F] p-7 sm:p-9">
              <h2 className="font-heading text-2xl font-semibold">Focus areas</h2>
              {!editing ? (
                <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="leader-focus-areas">
                  {bio.focusAreas.map((area) => (
                    <p key={area} className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-slate-300">
                      <CheckCircle2 className="mt-0.5 shrink-0 text-amber-400" size={17} /> {area}
                    </p>
                  ))}
                </div>
              ) : (
                <>
                  <input
                    value={draft.focusAreas}
                    onChange={(event) => setDraft((current) => ({ ...current, focusAreas: event.target.value }))}
                    data-testid="leader-focus-areas-input"
                    className="mt-6 w-full rounded-2xl border border-white/10 bg-[#070C14] p-4 text-white outline-none focus:border-amber-400"
                    placeholder="Separate focus areas with commas"
                  />
                  <p className="mt-3 text-xs text-slate-500">Separate each focus area with a comma.</p>
                </>
              )}
            </div>

            {override && (
              <button type="button" onClick={resetBio} data-testid="reset-leader-bio-button" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 transition-colors hover:border-amber-400 hover:text-amber-300">
                <RotateCcw size={14} /> Reset to original
              </button>
            )}
          </motion.div>
        </section>
      </div>
    </main>
  );
}
